import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Eye, EyeOff, Mail } from "lucide-react";
import { MrDriveAvatar } from "@/components/MrDriveAvatar";
import { OpenInBrowserCard } from "@/components/NextStep";
import { detectInAppBrowser, isStandalone } from "@/lib/device";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { CitySelect } from "@/components/CitySelect";
import { VISITOR_KIND_OPTIONS } from "@/lib/people-constants";
import { getRedirectIntent } from "@/lib/redirect-intent";
import { postAuthDestination } from "@/lib/onboarding-state";
import {
  mailboxUrl,
  suggestEmailFix,
  cooldownRemaining,
  isEmailNotConfirmed,
} from "@/lib/email-helpers";
import { detectPlatform } from "@/lib/device";

/** Таймер повторной отправки письма: возвращает остаток секунд и функцию «отправлено». */
function useResendCooldown() {
  const [sentAt, setSentAt] = useState<number | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const left = cooldownRemaining(sentAt, now);
  useEffect(() => {
    if (left <= 0) return;
    const t = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(t);
  }, [left]);
  return {
    left,
    markSent: () => {
      const t = Date.now();
      setSentAt(t);
      setNow(t);
    },
  };
}

function ResendButton({ email, label }: { email: string; label: string }) {
  const [loading, setLoading] = useState(false);
  const { left, markSent } = useResendCooldown();
  async function handle() {
    setLoading(true);
    const { error } = await supabase.auth.resend({
      type: "signup",
      email,
      options: { emailRedirectTo: confirmRedirectUrl(email) },
    });
    setLoading(false);
    if (error) {
      toast.error(getErrorMessage(error));
      return;
    }
    markSent();
    toast.success("Письмо отправлено");
  }
  return (
    <div className="space-y-1">
      <Button type="button" variant="outline" className="w-full" onClick={handle} disabled={loading || left > 0}>
        {loading ? "Отправляем..." : label}
      </Button>
      {left > 0 && (
        <p className="text-center text-xs text-td-muted">Можно отправить снова через {left} с</p>
      )}
    </div>
  );
}

/** Ссылка из письма ведёт на /auth/callback: там сессия подхватывается, а если её нет — экран «почта подтверждена». */
function confirmRedirectUrl(email: string): string | undefined {
  if (typeof window === "undefined") return undefined;
  const params = new URLSearchParams({ confirm: "1", email });
  const intent = getRedirectIntent();
  if (intent) params.set("redirect", intent);
  return `${window.location.origin}/auth/callback?${params.toString()}`;
}

function getErrorMessage(err: unknown): string {
  // Временный диагностический лог — оставить, пока не подтвердим, что реальные
  // ошибки регистрации показываются корректно.
  if (typeof console !== "undefined") console.error("[auth] error:", err);
  if (!err) return "Произошла ошибка, попробуйте ещё раз";
  if (typeof err === "string") return err || "Произошла ошибка, попробуйте ещё раз";
  if (typeof err === "object") {
    const anyErr = err as {
      message?: unknown;
      error_description?: unknown;
      msg?: unknown;
      error?: unknown;
      status?: unknown;
      code?: unknown;
      name?: unknown;
    };
    const msg =
      (typeof anyErr.message === "string" && anyErr.message) ||
      (typeof anyErr.error_description === "string" && anyErr.error_description) ||
      (typeof anyErr.msg === "string" && anyErr.msg) ||
      (typeof anyErr.error === "string" && anyErr.error) ||
      "";
    if (msg) return msg;
    // Фоллбек: покажем хотя бы статус/код, чтобы не было пустого "{}"
    const status = anyErr.status ?? anyErr.code;
    if (status) return `Ошибка сервера (${String(status)}). Попробуйте позже.`;
    try {
      const dump = JSON.stringify(err);
      if (dump && dump !== "{}") return dump;
    } catch {}
  }
  return "Произошла ошибка, попробуйте ещё раз";
}

const signInSchema = z.object({
  email: z.string().email("Введите корректный email"),
  password: z.string().min(8, "Минимум 8 символов"),
});

const EMAIL_LIKE_RE = /@[^\s@]+\.[^\s@]+/;

const signUpSchema = z.object({
  email: z.string().email("Введите корректный email"),
  password: z.string().min(8, "Минимум 8 символов"),
  full_name: z
    .string()
    .min(2, "Введите имя и фамилию")
    .refine((v) => !EMAIL_LIKE_RE.test(v), "Введите имя, а не email")
    .refine(
      (v) => v.trim().split(/\s+/).filter((p) => p.length >= 2).length >= 2,
      "Укажите имя и фамилию — например, Евгения Изотова",
    ),

  city: z.string().min(2, "Введите город"),
});

function PasswordInput(props: React.InputHTMLAttributes<HTMLInputElement>) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <Input {...props} type={show ? "text" : "password"} className="pr-10" />
      <button
        type="button"
        onClick={() => setShow((s) => !s)}
        aria-label={show ? "Скрыть пароль" : "Показать пароль"}
        className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-foreground"
        tabIndex={-1}
      >
        {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </div>
  );
}

type SignInValues = z.infer<typeof signInSchema>;
type SignUpValues = z.infer<typeof signUpSchema>;

export const Route = createFileRoute("/auth")({
  head: () => ({ meta: [
    { title: "Вход и регистрация — TurkeyDrive" },
    { name: "description", content: "Вход и регистрация в сообществе TurkeyDrive." },
    { property: "og:title", content: "Вход и регистрация — TurkeyDrive" },
    { property: "og:description", content: "Вход и регистрация в сообществе TurkeyDrive." },
    { property: "og:type", content: "website" },
    { name: "twitter:card", content: "summary" },
    { name: "robots", content: "noindex" },
  ] }),
  component: AuthPage,
});

const IN_APP_CONTINUE_KEY = "td_inapp_continue_here";

function AuthPage() {
  const { user } = useAuth();
  const [showForms, setShowForms] = useState(false);
  const [fromEvent, setFromEvent] = useState(false);
  const [authTab, setAuthTab] = useState<"signin" | "signup">("signin");
  const [signupMode, setSignupMode] = useState(false);
  const [inAppGate, setInAppGate] = useState(false);
  const [confirmedEmail, setConfirmedEmail] = useState<string | null>(null);

  useEffect(() => {
    let skipped = false;
    try {
      skipped = sessionStorage.getItem(IN_APP_CONTINUE_KEY) === "1";
    } catch {
      /* ignore */
    }
    setInAppGate(!skipped && !!detectInAppBrowser() && !isStandalone());
  }, []);

  useEffect(() => {
    try {
      const url = new URL(window.location.href);
      const target = url.searchParams.get("redirect") ?? getRedirectIntent();
      setFromEvent(
        Boolean(target && (target.startsWith("/e/") || target.includes("event="))),
      );

      if (url.searchParams.get("confirmed") === "1") {
        setConfirmedEmail(url.searchParams.get("email") ?? "");
        setAuthTab("signin");
        setShowForms(true);
        setInAppGate(false);
        return;
      }
      const mode = url.searchParams.get("mode");
      if (mode === "signup" || mode === "signin") {
        setAuthTab(mode);
        setSignupMode(mode === "signup");
      }
    } catch {
      /* ignore */
    }
  }, []);


  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    void postAuthDestination(user.id).then((dest) => {
      if (!cancelled) window.location.replace(dest);
    });
    return () => {
      cancelled = true;
    };
  }, [user]);

  if (inAppGate && !user && confirmedEmail === null) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-td-teal/10 via-white to-white px-4 py-10">
        <div className="mx-auto max-w-[420px] rounded-2xl border border-td-line bg-white p-6 shadow-sm">
          <OpenInBrowserCard
            onContinue={() => {
              try {
                sessionStorage.setItem(IN_APP_CONTINUE_KEY, "1");
              } catch {
                /* ignore */
              }
              setInAppGate(false);
            }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-td-teal/10 via-white to-white px-4 py-10">
      <div className="mx-auto flex min-h-[80vh] max-w-[420px] flex-col">
        <div className="mb-2 text-left">
          {showForms ? (
            <button
              type="button"
              onClick={() => setShowForms(false)}
              className="inline-flex items-center text-[13px] text-td-muted transition-colors hover:text-td-dark"
            >
              ← Назад
            </button>
          ) : (
            <Link
              to="/"
              className="inline-flex items-center text-[13px] text-td-muted transition-colors hover:text-td-dark"
            >
              ← На главную
            </Link>
          )}
        </div>

        <div className="mb-8 mt-6 flex flex-col items-center text-center">
          <MrDriveAvatar variant="home" size={128} className="mb-5" />
          <h1 className="font-brand text-[26px] font-bold leading-tight text-td-dark">
            Добро пожаловать в TurkeyDrive
          </h1>
          <p className="mt-2 text-sm text-td-muted">{signupMode ? "Создай профиль за минуту" : "Войдите, чтобы продолжить"}</p>
        </div>

        {fromEvent && (
          <div className="mb-5 rounded-2xl border border-td-line bg-td-soft px-4 py-3 text-center text-[13px] text-td-dark">
            Это шаг входа в сообщество TurkeyDrive. После регистрации или входа мы вернём тебя к мероприятию.
          </div>
        )}



        {!showForms ? (
          <div className="space-y-3">
            <GoogleButton emphasized />
            <GoogleConsentNote />
            <p className="text-center text-[12px] text-td-muted">
              Быстрее всего — без подтверждения по почте
            </p>
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={() => setShowForms(true)}
            >
              <Mail className="mr-2 h-4 w-4" />
              Продолжить с Email
            </Button>
          </div>

        ) : (
          <Tabs
            value={authTab}
            onValueChange={(v) => setAuthTab(v as "signin" | "signup")}
            className="w-full"
          >
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="signin">Вход</TabsTrigger>
              <TabsTrigger value="signup">Регистрация</TabsTrigger>
            </TabsList>
            <TabsContent value="signin">
              <SignInForm defaultEmail={confirmedEmail ?? ""} confirmed={confirmedEmail !== null} />
            </TabsContent>
            <TabsContent value="signup">
              <SignUpForm />
            </TabsContent>
          </Tabs>
        )}
      </div>
    </div>
  );
}

function SignInForm({ defaultEmail = "", confirmed = false }: { defaultEmail?: string; confirmed?: boolean }) {
  const [isLoading, setIsLoading] = useState(false);
  const [showReset, setShowReset] = useState(false);
  const [unconfirmedEmail, setUnconfirmedEmail] = useState<string | null>(null);
  const form = useForm<SignInValues>({
    resolver: zodResolver(signInSchema),
    defaultValues: { email: defaultEmail, password: "" },
  });
  useEffect(() => {
    if (defaultEmail) form.setValue("email", defaultEmail);
  }, [defaultEmail, form]);

  async function onSubmit(values: SignInValues) {
    setIsLoading(true);
    const { data: signIn, error } = await supabase.auth.signInWithPassword({
      email: values.email,
      password: values.password,
    });
    setIsLoading(false);

    if (error) {
      if (isEmailNotConfirmed(error)) {
        setUnconfirmedEmail(values.email);
        return;
      }
      toast.error(getErrorMessage(error));
      return;
    }

    toast.success("Вы вошли в аккаунт");
    // Don't wait for the auth-context effect: navigate right away.
    window.location.replace(signIn.user ? await postAuthDestination(signIn.user.id) : "/home");
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{confirmed ? "Почта подтверждена ✓" : "Вход в аккаунт"}</CardTitle>
        <CardDescription>
          {confirmed ? "Теперь войди с email и паролем" : "Введите email и пароль, чтобы войти"}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {unconfirmedEmail && (
          <div className="space-y-3 rounded-2xl border border-td-teal/30 bg-td-teal/5 p-4 text-sm text-td-dark">
            <p>
              Почта ещё не подтверждена. Мы можем отправить письмо снова на{" "}
              <span className="font-semibold">{unconfirmedEmail}</span>.
            </p>
            <ResendButton email={unconfirmedEmail} label="Отправить письмо" />
          </div>
        )}
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="signin-email">Email</Label>
            <Input id="signin-email" type="email" {...form.register("email")} />
            {form.formState.errors.email && (
              <p className="text-sm text-red-500">{form.formState.errors.email.message}</p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="signin-password">Пароль</Label>
            <PasswordInput id="signin-password" {...form.register("password")} />
            {form.formState.errors.password && (
              <p className="text-sm text-red-500">{form.formState.errors.password.message}</p>
            )}
            <div className="text-right">
              <button
                type="button"
                onClick={() => setShowReset((s) => !s)}
                className="text-xs text-muted-foreground underline hover:text-td-dark"
              >
                {showReset ? "Скрыть" : "Забыли пароль?"}
              </button>
            </div>
          </div>
          <Button type="submit" className="w-full" disabled={isLoading}>
            {isLoading ? "Вход..." : "Войти"}
          </Button>
        </form>
        {showReset && <ForgotPasswordForm defaultEmail={form.getValues("email")} />}
        <div className="relative py-2">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-card px-2 text-muted-foreground">или</span>
          </div>
        </div>
        <GoogleButton />
        <GoogleConsentNote />
      </CardContent>
    </Card>
  );
}

function ForgotPasswordForm({ defaultEmail }: { defaultEmail?: string }) {
  const [email, setEmail] = useState(defaultEmail ?? "");
  const [isLoading, setIsLoading] = useState(false);
  const [sent, setSent] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email || !/^\S+@\S+\.\S+$/.test(email)) {
      toast.error("Введите корректный email");
      return;
    }
    setIsLoading(true);
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo:
        typeof window !== "undefined" ? `${window.location.origin}/reset-password` : undefined,
    });
    setIsLoading(false);
    if (error) {
      toast.error(getErrorMessage(error));
      return;
    }
    setSent(true);
  }

  if (sent) {
    return (
      <div className="rounded-md border border-td-yellow/40 bg-td-yellow/10 p-3 text-sm text-td-dark">
        Проверьте почту — мы отправили ссылку для сброса пароля.
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="space-y-2 rounded-md border bg-muted/30 p-3">
      <Label htmlFor="reset-email" className="text-xs">
        Email для восстановления
      </Label>
      <Input
        id="reset-email"
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="you@example.com"
      />
      <Button type="submit" variant="outline" className="w-full" disabled={isLoading}>
        {isLoading ? "Отправляем..." : "Отправить ссылку"}
      </Button>
    </form>
  );
}


export function SignUpSuccessState({ email, onEdit }: { email: string; onEdit?: () => void }) {
  const mailUrl = mailboxUrl(email);
  const isAndroid = typeof navigator !== "undefined" && detectPlatform() === "android";
  return (
    <Card>
      <CardContent className="space-y-6 pt-6">
        <div className="flex flex-col items-center space-y-3 text-center">
          <MrDriveAvatar variant="home" size={80} />
          <h2 className="font-brand text-2xl font-bold leading-tight text-td-dark">
            Остался один шаг: подтверди почту
          </h2>
          <p className="text-base text-td-muted">
            Письмо ушло на <span className="font-semibold text-td-dark">{email}</span>
          </p>
        </div>
        <ol className="list-decimal space-y-2 pl-5 text-base text-td-dark">
          <li>Открой почту.</li>
          <li>
            Найди письмо «Подтвердите почту для TurkeyDrive». Не пришло за минуту — загляни в «Спам» или «Промоакции».
          </li>
          <li>Нажми кнопку в письме — и ты в TurkeyDrive.</li>
        </ol>
        <p className="rounded-2xl bg-td-teal/5 p-3 text-sm text-td-dark">
          Письмо открылось в Instagram или другом встроенном браузере? Нажми «•••» →{" "}
          {isAndroid ? "«Открыть в Chrome»" : "«Открыть в Safari»"}.
        </p>
        <div className="space-y-3">
          {mailUrl && (
            <Button asChild className="w-full">
              <a href={mailUrl} target="_blank" rel="noopener noreferrer">
                <Mail className="mr-2 h-4 w-4" />
                Открыть почту
              </a>
            </Button>
          )}
          <ResendButton email={email} label="Отправить письмо ещё раз" />
          {onEdit && (
            <p className="text-center text-sm text-td-muted">
              Неверный адрес?{" "}
              <button type="button" onClick={onEdit} className="text-td-teal underline">
                Изменить
              </button>
            </p>
          )}
        </div>
        <div className="space-y-2 border-t pt-4">
          <p className="text-center text-xs text-td-muted">Проще: войти через Google</p>
          <GoogleButton />
        </div>
      </CardContent>
    </Card>
  );
}

function SignUpForm() {
  const [isLoading, setIsLoading] = useState(false);
  const [consent, setConsent] = useState(false);
  const [visitorKind, setVisitorKind] = useState<string>("resident");
  const [signupSuccessEmail, setSignupSuccessEmail] = useState<string | null>(null);
  const [emailFix, setEmailFix] = useState<string | null>(null);
  const form = useForm<SignUpValues>({
    resolver: zodResolver(signUpSchema),
    defaultValues: { email: "", password: "", full_name: "", city: "" },
  });

  async function onSubmit(values: SignUpValues) {
    if (!consent) {
      toast.error("Подтвердите согласие, чтобы зарегистрироваться");
      return;
    }
    setIsLoading(true);
    const { error } = await supabase.auth.signUp({
      email: values.email,
      password: values.password,
      options: {
        emailRedirectTo: confirmRedirectUrl(values.email),

        data: {
          full_name: values.full_name,
          city: values.city,
          visitor_kind: visitorKind,
        },
      },
    });
    setIsLoading(false);

    if (error) {
      toast.error(getErrorMessage(error));
      return;
    }

    setSignupSuccessEmail(values.email);
    // Данные формы оставляем (кроме пароля) — на случай «Неверный адрес? Изменить».
    form.setValue("password", "");
  }

  if (signupSuccessEmail) {
    return <SignUpSuccessState email={signupSuccessEmail} onEdit={() => setSignupSuccessEmail(null)} />;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Регистрация</CardTitle>
        <CardDescription>Создайте аккаунт, чтобы присоединиться к сообществу</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="signup-full_name">Имя и фамилия</Label>
            <Input id="signup-full_name" placeholder="Евгения Изотова" {...form.register("full_name")} />
            {form.formState.errors.full_name && (
              <p className="text-sm text-red-500">{form.formState.errors.full_name.message}</p>
            )}
          </div>
          <div className="space-y-2">
            <Label>Тип пребывания</Label>
            <div className="flex flex-wrap gap-2">
              {VISITOR_KIND_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setVisitorKind(opt.value)}
                  className={`rounded-full border px-3 py-1.5 text-sm transition ${
                    visitorKind === opt.value
                      ? "border-td-teal bg-td-teal text-white"
                      : "border-td-line bg-white text-td-muted hover:border-td-teal/60"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="signup-city">Город в Турции (где вы или куда приезжаете)</Label>
            <CitySelect
              id="signup-city"
              value={form.watch("city")}
              onChange={(v) => form.setValue("city", v, { shouldValidate: true })}
            />
            {form.formState.errors.city && (
              <p className="text-sm text-red-500">{form.formState.errors.city.message}</p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="signup-email">Email</Label>
            <Input
              id="signup-email"
              type="email"
              {...form.register("email", {
                onBlur: (e) => setEmailFix(suggestEmailFix(e.target.value)),
                onChange: () => setEmailFix(null),
              })}
            />
            {emailFix && (
              <div className="flex items-center justify-between gap-2 rounded-xl bg-td-teal/5 px-3 py-2 text-sm text-td-dark">
                <span className="min-w-0 break-all">Ты имел(а) в виду {emailFix}?</span>
                <button
                  type="button"
                  className="shrink-0 font-semibold text-td-teal"
                  onClick={() => {
                    form.setValue("email", emailFix, { shouldValidate: true });
                    setEmailFix(null);
                  }}
                >
                  Исправить
                </button>
              </div>
            )}
            {form.formState.errors.email && (
              <p className="text-sm text-red-500">{form.formState.errors.email.message}</p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="signup-password">Пароль</Label>
            <PasswordInput id="signup-password" {...form.register("password")} />
            {form.formState.errors.password && (
              <p className="text-sm text-red-500">{form.formState.errors.password.message}</p>
            )}
          </div>
          <div className="flex items-start gap-2.5 rounded-md border bg-muted/30 p-3">
            <Checkbox
              id="signup-consent"
              checked={consent}
              onCheckedChange={(v) => setConsent(v === true)}
              className="mt-0.5"
            />
            <Label
              htmlFor="signup-consent"
              className="text-[12px] font-normal leading-snug text-td-muted md:text-[13px]"
            >
              Мне есть 18 лет. Я принимаю{" "}
              <a
                href="/legal/kullanim-kosullari"
                target="_blank"
                rel="noopener noreferrer"
                className="text-td-teal underline"
              >
                Пользовательское соглашение
              </a>
              , ознакомлен(а) с{" "}
              <a
                href="/legal/aydinlatma-metni"
                target="_blank"
                rel="noopener noreferrer"
                className="text-td-teal underline"
              >
                Уведомлением об обработке данных
              </a>{" "}
              и даю{" "}
              <a
                href="/legal/acik-riza"
                target="_blank"
                rel="noopener noreferrer"
                className="text-td-teal underline"
              >
                Согласие на обработку данных
              </a>
              , включая передачу данных за пределы Турции.
            </Label>
          </div>
          <Button type="submit" className="w-full" disabled={isLoading || !consent}>
            {isLoading ? "Регистрация..." : "Зарегистрироваться"}
          </Button>
        </form>
        <div className="relative py-2">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-card px-2 text-muted-foreground">или</span>
          </div>
        </div>
        <GoogleButton disabled={!consent} />
        {!consent && (
          <p className="text-center text-[12px] text-td-muted">
            Отметьте согласие выше, чтобы продолжить регистрацию.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function GoogleConsentNote() {
  return (
    <p className="text-[12px] leading-snug text-td-muted md:text-[13px]">
      Нажимая «Продолжить с Google», ты подтверждаешь, что тебе есть 18 лет, принимаешь{" "}
      <a href="/legal/kullanim-kosullari" target="_blank" rel="noopener noreferrer" className="text-td-teal underline">Пользовательское соглашение</a>
      , знакомишься с{" "}
      <a href="/legal/aydinlatma-metni" target="_blank" rel="noopener noreferrer" className="text-td-teal underline">Уведомлением об обработке данных</a>{" "}
      и даёшь{" "}
      <a href="/legal/acik-riza" target="_blank" rel="noopener noreferrer" className="text-td-teal underline">Согласие на обработку данных</a>
      , включая передачу данных за пределы Турции.
    </p>
  );
}

function GoogleButton({
  disabled = false,
  emphasized = false,
}: {
  disabled?: boolean;
  emphasized?: boolean;
}) {
  const [isLoading, setIsLoading] = useState(false);

  async function handleGoogle() {
    setIsLoading(true);
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo:
          typeof window !== "undefined"
            ? `${window.location.origin}/auth/callback${
                getRedirectIntent() ? `?redirect=${encodeURIComponent(getRedirectIntent()!)}` : ""
              }`
            : undefined,
      },
    });
    setIsLoading(false);

    if (error) {
      toast.error(getErrorMessage(error));
    }
  }

  return (
    <Button
      type="button"
      variant={emphasized ? "default" : "outline"}
      style={emphasized ? { backgroundColor: "var(--accent-500)", color: "#fff" } : undefined}
      className={
        emphasized
          ? "h-12 w-full rounded-full border-0 text-base font-semibold hover:opacity-90"
          : "w-full"
      }
      onClick={handleGoogle}
      disabled={isLoading || disabled}
    >


      <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
        <path
          d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
          fill="#4285F4"
        />
        <path
          d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
          fill="#34A853"
        />
        <path
          d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
          fill="#FBBC05"
        />
        <path
          d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
          fill="#EA4335"
        />
      </svg>
      {isLoading ? "Секунду…" : "Продолжить с Google"}
    </Button>
  );
}
